import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Upload, Trash2, Image, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TREATS = [
  "Strawberries",
  "Cake Pops",
  "Macarons",
  "Rice Krispies",
  "Oreos",
  "Pretzels",
  "Madeleines",
  "Wafers",
];

const TreatImageCard = ({ name, imageData, onUpload, onDelete }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!imageData?.file_id) { setPreview(null); return; }
    let url;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/treats-images/${imageData.file_id}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setPreview(url);
      } catch { setPreview(null); }
    };
    load();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [imageData?.file_id]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Images only"); return; }

    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(`${API}/admin/treats-images/${name}`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${name} image updated`);
      onUpload();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/treats-images/${name}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${name} image removed — default will show`);
      onDelete();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="border border-[rgba(212,175,55,0.15)] bg-[#121212] overflow-hidden" data-testid={`treat-image-card-${name.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="aspect-square relative group bg-[#0F0F0F]">
        {preview ? (
          <>
            <img src={preview} alt={name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
              <button
                onClick={() => inputRef.current?.click()}
                className="p-3 bg-[#D4AF37] text-[#050505] hover:bg-[#F7E7CE] transition-colors"
                title="Replace image"
              >
                <Upload className="w-5 h-5" />
              </button>
              <button
                onClick={handleDelete}
                className="p-3 bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-colors"
                title="Remove image"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </>
        ) : (
          <div
            className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-[#121212] transition-colors"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="w-10 h-10 text-[#D4AF37]/30 mb-3" />
            <p className="text-[#A1A1AA] text-sm">Upload Image</p>
            <p className="text-[#52525B] text-xs mt-1">Click to choose file</p>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-[#050505]/80 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      <div className="p-4 flex items-center justify-between">
        <div>
          <h3 className="text-[#F5F5F0] font-semibold text-sm">{name}</h3>
          <p className="text-[#52525B] text-xs mt-0.5">
            {imageData ? (
              <span className="text-[#D4AF37] flex items-center gap-1">
                <Check className="w-3 h-3" /> Custom image
              </span>
            ) : (
              "Using default"
            )}
          </p>
        </div>
        {preview && (
          <span className="text-[#52525B] text-[10px] uppercase tracking-wider">
            {imageData?.original_filename?.substring(0, 20)}
          </span>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
};

const AdminTreatsImages = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/public/treats-images`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImages(res.data);
    } catch {
      toast.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchImages(); }, []);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-treats-images-page">
      <div className="border-b border-[rgba(212,175,55,0.15)] bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-['Playfair_Display'] text-xl text-[#F5F5F0]">Treats Images</h1>
                <p className="text-[#52525B] text-sm">Upload your own images for each treat on the Treats page</p>
              </div>
            </div>
            <button
              onClick={fetchImages}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors border border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.4)]"
              data-testid="refresh-images"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 p-4 border border-[rgba(212,175,55,0.2)] bg-[#D4AF37]/5">
          <p className="text-[#A1A1AA] text-sm">
            Upload your own photos for each treat below. Images will immediately appear on the{" "}
            <a href="/treats" target="_blank" className="text-[#D4AF37] underline">Treats page</a>.
            If no custom image is uploaded, a default placeholder will be shown.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {TREATS.map((name) => (
              <TreatImageCard
                key={name}
                name={name}
                imageData={images[name] || null}
                onUpload={fetchImages}
                onDelete={fetchImages}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTreatsImages;
